import { useState, useEffect, useCallback, useRef } from "react";
import { soApi } from "@/webapp/site-owners/api";
import { errorText } from "@/lib/api";
import { Card, Label, Field, Select, Btn, Pill, Notice, when } from "@/webapp/shared/ui";

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/gate/plate-event`;
const POLL_MS = 8000;
const SITE_OPTS = [["north", "North"], ["central", "Central"], ["south", "South"]];
const DIR_OPTS = [["auto", "Auto (in, or out if on site)"], ["in", "Entry lane"], ["out", "Exit lane"]];
const OUTCOME = {
  entry: ["Entry recorded", "bg-emerald-50 text-emerald-700"], exit: ["Departure recorded", "bg-blue-50 text-blue-700"],
  unmatched: ["Unrecognised", "bg-red-50 text-red-600"], ambiguous: ["Ambiguous", "bg-amber-50 text-amber-700"],
  low_confidence: ["Low confidence", "bg-amber-50 text-amber-700"], not_onsite: ["Not on site", "bg-amber-50 text-amber-700"],
  already_onsite: ["Already on site", "bg-slate-100 text-slate-600"], duplicate: ["Duplicate", "bg-slate-100 text-slate-500"],
  dismissed: ["Dismissed", "bg-slate-100 text-slate-500"],
};

export function PlateEdit({ booking, reload }) {
  const [v, setV] = useState(booking.plate || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setV(booking.plate || ""); }, [booking.plate]);
  const save = async () => { setBusy(true); setErr(""); try { await soApi.setPlate(booking.id, v); await reload(); } catch (e) { setErr(errorText(e, "Invalid plate.")); } finally { setBusy(false); } };
  const dirty = v.toUpperCase().replace(/[^A-Z0-9]/g, "") !== (booking.plate || "");
  return (
    <span className="flex items-center gap-1.5">
      <input data-testid={`so-plate-input-${booking.ref}`} value={v} onChange={(e) => setV(e.target.value.toUpperCase())} placeholder="Plate" maxLength={12}
        className="h-8 w-28 rounded-md border border-slate-200 bg-[#F7F8FA] px-2 font-mono text-xs font-bold text-[#0A234A] outline-none focus:border-[#FF5722]/60" />
      {dirty && <button data-testid={`so-plate-save-${booking.ref}`} disabled={busy} onClick={save} className="text-xs font-bold text-[#FF5722] disabled:opacity-40">Save</button>}
      {err && <span className="text-xs font-semibold text-red-600" data-testid={`so-plate-error-${booking.ref}`}>{err}</span>}
    </span>
  );
}

function ResolveControls({ read, bookings, onDone }) {
  const opts = bookings.filter((b) => b.site === read.site && ["booked", "verified", "onsite"].includes(b.status));
  const [bid, setBid] = useState("");
  const [dir, setDir] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (payload) => { setBusy(true); setErr(""); try { await soApi.resolveRead(read.id, payload); await onDone(); } catch (e) { setErr(errorText(e, "Could not resolve.")); } finally { setBusy(false); } };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <select data-testid={`so-gate-resolve-select-${read.id}`} value={bid} onChange={(e) => setBid(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
        <option value="">Match to booking…</option>
        {opts.map((b) => <option key={b.id} value={b.id}>{b.ref} · {b.stall} · {b.party} · {b.status}</option>)}
      </select>
      <select data-testid={`so-gate-resolve-direction-${read.id}`} value={dir} onChange={(e) => setDir(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
        <option value="">Lane as read ({read.direction})</option>
        <option value="in">Record entry</option>
        <option value="out">Record departure</option>
      </select>
      <Btn testid={`so-gate-resolve-${read.id}`} disabled={!bid || busy} onClick={() => run(dir ? { booking_id: bid, direction: dir } : { booking_id: bid })} className="!px-3 !py-1 text-xs">Apply</Btn>
      <Btn ghost testid={`so-gate-dismiss-${read.id}`} disabled={busy} onClick={() => run({ dismiss: true })} className="!px-3 !py-1 text-xs">Dismiss</Btn>
      {err && <span className="text-xs font-semibold text-red-600" data-testid={`so-gate-resolve-error-${read.id}`}>{err}</span>}
    </div>
  );
}

function ReadRow({ r, bookings, onDone }) {
  const [label, tone] = OUTCOME[r.outcome] || [r.outcome, "bg-slate-100 text-slate-600"];
  return (
    <li className={`rounded-lg border p-3 ${r.needs_review ? "border-amber-200 bg-amber-50/40" : "border-slate-100"}`} data-testid={`so-gate-read-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm font-bold tracking-wider text-[#0A234A]" data-testid={`so-gate-read-plate-${r.id}`}>{r.plate}</span>
        <Pill tone={tone} testid={`so-gate-read-outcome-${r.id}`}>{label}</Pill>
        {r.booking_ref && <span className="text-xs font-semibold text-slate-500">{r.booking_ref}</span>}
        {r.resolved_at && <Pill tone="bg-slate-100 text-slate-600" testid={`so-gate-read-resolved-${r.id}`}>Resolved {when(r.resolved_at)}</Pill>}
        <span className="ml-auto text-xs text-slate-400">{r.site} · {r.direction} · {r.source}{r.confidence != null ? ` · ${Math.round(r.confidence * 100)}%` : ""} · {when(r.created_at)}</span>
      </div>
      {r.reason && <p className="mt-1 text-xs text-slate-500">{r.reason}</p>}
      {r.needs_review && <ResolveControls read={r} bookings={bookings} onDone={onDone} />}
    </li>
  );
}

function GateFeed({ gate, bookings, onDone }) {
  const [onlyReview, setOnlyReview] = useState(false);
  const reads = onlyReview ? gate.reads.filter((r) => r.needs_review) : gate.reads;
  return (
    <Card className="p-6 lg:col-span-3" testid="so-gate-feed">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold text-[#0A234A]">Gate plate reads</h2>
        <Pill tone={gate.review ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"} testid="so-gate-review-count">{gate.review} to review</Pill>
        <Pill tone={gate.chain.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"} testid="so-gate-chain">{gate.chain.ok ? `Audit chain verified · ${gate.chain.checked}` : "Audit chain broken"}</Pill>
        <button data-testid="so-gate-filter-review" onClick={() => setOnlyReview((v) => !v)} className="ml-auto text-xs font-bold text-[#FF5722]">{onlyReview ? "Show all" : "Needs review only"}</button>
      </div>
      <ul className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
        {reads.map((r) => <ReadRow key={r.id} r={r} bookings={bookings} onDone={onDone} />)}
        {reads.length === 0 && <li className="text-sm text-slate-400" data-testid="so-gate-empty">No plate reads yet. Connect a camera with a gate key, or send a test read.</li>}
      </ul>
    </Card>
  );
}

function GateTest({ onDone }) {
  const [f, setF] = useState({ site: "north", plate: "", direction: "auto", confidence: "0.95" });
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const send = async () => {
    setMsg(null);
    try { const r = await soApi.testRead({ ...f, confidence: Number(f.confidence) }); setMsg({ ok: ["entry", "exit"].includes(r.read.outcome), t: `${r.read.plate}: ${(OUTCOME[r.read.outcome] || [r.read.outcome])[0]}${r.read.reason ? ` — ${r.read.reason}` : ""}` }); await onDone(); }
    catch (e) { setMsg({ ok: false, t: errorText(e, "Test read failed.") }); }
  };
  return (
    <Card className="p-6" testid="so-gate-test">
      <h2 className="mb-1 font-display text-lg font-bold text-[#0A234A]">Send a test read</h2>
      <p className="mb-4 text-xs text-slate-500">Runs the same matching as a live camera. Logged as source “test”.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Site</Label><Select data-testid="so-gate-test-site" value={f.site} onChange={set("site")}>{SITE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><Label>Lane</Label><Select data-testid="so-gate-test-direction" value={f.direction} onChange={set("direction")}>{DIR_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><Label>Plate</Label><Field data-testid="so-gate-test-plate" value={f.plate} onChange={set("plate")} placeholder="SBX0803" maxLength={12} /></div>
        <div><Label>Confidence</Label><Field data-testid="so-gate-test-confidence" type="number" step="0.05" min="0" max="1" value={f.confidence} onChange={set("confidence")} /></div>
      </div>
      <Btn primary testid="so-gate-test-send" className="mt-4 w-full" disabled={!f.plate.trim()} onClick={send}>Send read</Btn>
      {msg && <div className="mt-3"><Notice tone={msg.ok ? "ok" : "error"} testid="so-gate-test-result">{msg.t}</Notice></div>}
    </Card>
  );
}

function NewKey({ onDone }) {
  const [f, setF] = useState({ site: "north", label: "", direction: "auto" });
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const create = async () => { setErr(""); setCopied(false); try { setCreated(await soApi.createKey(f)); await onDone(); } catch (e) { setErr(errorText(e, "Could not create key.")); } };
  const copy = async () => { try { await navigator.clipboard.writeText(created.key); setCopied(true); } catch { setCopied(false); } };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><Label>Site</Label><Select data-testid="so-gate-key-site" value={f.site} onChange={set("site")}>{SITE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><Label>Lane</Label><Select data-testid="so-gate-key-direction" value={f.direction} onChange={set("direction")}>{DIR_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><Label>Label</Label><Field data-testid="so-gate-key-label" value={f.label} onChange={set("label")} placeholder="North entry cam" maxLength={60} /></div>
      </div>
      <Btn primary testid="so-gate-key-create" onClick={create}>Create gate key</Btn>
      {err && <Notice testid="so-gate-key-error">{err}</Notice>}
      {created && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="so-gate-key-created">
          <p className="text-xs font-bold text-emerald-700">Copy this key now. It will not be shown again.</p>
          <code className="mt-1 block break-all font-mono text-xs text-[#0A234A]" data-testid="so-gate-key-secret">{created.key}</code>
          <button data-testid="so-gate-key-copy" onClick={copy} className="mt-2 text-xs font-bold text-[#FF5722]">{copied ? "Copied" : "Copy key"}</button>
        </div>
      )}
    </div>
  );
}

function GateKeys({ keys, onDone }) {
  const [err, setErr] = useState("");
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.label}"? Cameras using it will stop working immediately.`)) return;
    setErr("");
    try { await soApi.revokeKey(k.id); await onDone(); } catch (e) { setErr(errorText(e, "Could not revoke key.")); }
  };
  return (
    <Card className="p-6 lg:col-span-2" testid="so-gate-keys">
      <h2 className="mb-1 font-display text-lg font-bold text-[#0A234A]">Gate keys</h2>
      <p className="mb-4 text-xs text-slate-500">Each camera or plate-reading service gets its own key, scoped to one site. Send reads to <code className="break-all font-mono text-[11px] text-[#0A234A]" data-testid="so-gate-endpoint">{ENDPOINT}</code> with header <code className="font-mono text-[11px]">x-gate-key</code>.</p>
      <NewKey onDone={onDone} />
      <ul className="mt-5 divide-y divide-slate-100">
        {keys.map((k) => (
          <li key={k.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm" data-testid={`so-gate-key-${k.id}`}>
            <span className="font-semibold text-[#0A234A]">{k.label}</span>
            <span className="text-xs text-slate-400">{k.site} · {k.direction} · {k.last_used_at ? `last read ${when(k.last_used_at)}` : "never used"}</span>
            {k.revoked_at ? <Pill tone="bg-slate-100 text-slate-500">Revoked</Pill> : <Btn danger testid={`so-gate-key-revoke-${k.id}`} className="ml-auto !px-3 !py-1 text-xs" onClick={() => revoke(k)}>Revoke</Btn>}
          </li>
        ))}
        {keys.length === 0 && <li className="py-2 text-sm text-slate-400">No gate keys yet.</li>}
      </ul>
      {err && <div className="mt-3"><Notice testid="so-gate-key-revoke-error">{err}</Notice></div>}
    </Card>
  );
}

export default function GatePanel({ bookings, reload }) {
  const [gate, setGate] = useState(null);
  const [err, setErr] = useState("");
  const top = useRef(null);
  const load = useCallback(async () => {
    try {
      const g = await soApi.gate();
      setGate(g); setErr("");
      const first = g.reads[0];
      if (first && top.current && first.id !== top.current && ["entry", "exit"].includes(first.outcome)) reload();
      top.current = first ? first.id : null;
    } catch (e) { setErr(errorText(e, "Could not load gate reads.")); }
  }, [reload]);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  const done = async () => { await Promise.all([load(), reload()]); };
  if (err) return <div className="mt-6"><Notice testid="so-gate-error">{err}</Notice></div>;
  if (!gate) return <p className="mt-6 text-sm text-slate-400" data-testid="so-gate-loading">Loading gate reads…</p>;
  return (
    <div className="mt-6 space-y-6" data-testid="so-gate-panel">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <GateFeed gate={gate} bookings={bookings} onDone={done} />
        <div className="lg:col-span-2"><GateTest onDone={done} /></div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5"><GateKeys keys={gate.keys} onDone={done} /></div>
    </div>
  );
}
